import { Fragment, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Search } from "lucide-react";
import AdminLayout from "layouts/AdminLayout";
import AdminCard from "components/admin/AdminCard";
import AdminStatCard from "components/admin/AdminStatCard";
import StatusBadge from "components/admin/StatusBadge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  AdminRegime,
  aprovarRegime,
  listAdminRegimes,
  pesquisarRegime,
  rejeitarRegime,
} from "services/admin/adminRegime.service";

const SELECT_CLASS = "h-9 rounded-md border border-input bg-background px-3 text-sm";

const TOM_STATUS: Record<string, "green" | "yellow" | "red"> = {
  aprovado: "green",
  pendente: "yellow",
  rejeitado: "red",
};

const TOM_CONFIANCA: Record<string, "green" | "yellow" | "red"> = {
  alta: "green",
  media: "yellow",
  baixa: "red",
};

const data = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

const mensagemErro = (err: unknown, padrao: string) =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
  (err as Error)?.message ||
  padrao;

const AdminRegimes = () => {
  const [regimes, setRegimes] = useState<AdminRegime[]>([]);
  const [validadeDias, setValidadeDias] = useState(0);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [status, setStatus] = useState("");
  const [pais, setPais] = useState("");
  const [novoPais, setNovoPais] = useState("");
  const [ocupado, setOcupado] = useState<number | "pesquisa" | null>(null);
  const [expandido, setExpandido] = useState<number | null>(null);

  const carregar = () => {
    setLoading(true);
    setErro(null);
    listAdminRegimes({ status, pais })
      .then((resp) => {
        setRegimes(resp.regimes);
        setValidadeDias(resp.validade_dias);
      })
      .catch((err) => {
        console.error("[AdminRegimes] erro:", err);
        setErro(
          mensagemErro(err, "Não foi possível carregar os regimes. Confira se o backend está publicado.")
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [status, pais]);

  const pesquisar = async () => {
    const iso = novoPais.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(iso)) {
      setErro("Informe o país em ISO-2, ex.: US.");
      return;
    }
    setOcupado("pesquisa");
    setErro(null);
    setAviso(null);
    try {
      const r = await pesquisarRegime(iso);
      setAviso(r.aviso);
      setNovoPais("");
    } catch (err) {
      setErro(mensagemErro(err, "Não foi possível iniciar a apuração."));
    } finally {
      setOcupado(null);
    }
  };

  const aprovar = async (r: AdminRegime) => {
    const temDivergencia = (r.divergencias || []).length > 0;

    // O backend devolve 409 quando as rodadas divergiram e ninguém confirmou.
    // Pedir a confirmação aqui evita o ciclo erro-e-tenta-de-novo.
    if (temDivergencia) {
      const ok = window.confirm(
        `As duas rodadas divergiram na apuração de ${r.pais_destino}:\n\n` +
          (r.divergencias || []).join("\n") +
          "\n\nAprovar mesmo assim? Isto passa a valer para a cobrança dos clientes."
      );
      if (!ok) return;
    }

    setOcupado(r.id);
    setErro(null);
    setAviso(null);
    try {
      const resp = await aprovarRegime(r.id, temDivergencia);
      setAviso(`${r.pais_destino} aprovado por ${resp.aprovado_por}.`);
      carregar();
    } catch (err) {
      setErro(mensagemErro(err, "Não foi possível aprovar."));
    } finally {
      setOcupado(null);
    }
  };

  const rejeitar = async (r: AdminRegime) => {
    const motivo = window.prompt(`Motivo para rejeitar a apuração de ${r.pais_destino}:`, "");
    if (motivo === null) return;

    setOcupado(r.id);
    setErro(null);
    setAviso(null);
    try {
      await rejeitarRegime(r.id, motivo.trim() || undefined);
      setAviso(`${r.pais_destino} rejeitado.`);
      carregar();
    } catch (err) {
      setErro(mensagemErro(err, "Não foi possível rejeitar."));
    } finally {
      setOcupado(null);
    }
  };

  const pendentes = regimes.filter((r) => r.status === "pendente").length;
  const vencidos = regimes.filter((r) => r.vencido && r.status === "aprovado").length;

  return (
    <AdminLayout
      title="Regimes de importação"
      subtitle="Apuração tarifária por destino — o que a IA apurou só vale para cobrança depois de aprovado aqui"
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <AdminStatCard
            label="Aguardando conferência"
            value={pendentes}
            icon={<AlertTriangle />}
            tone={pendentes > 0 ? "danger" : "default"}
          />
          <AdminStatCard
            label="Aprovados vencidos"
            value={vencidos}
            icon={<AlertTriangle />}
            tone={vencidos > 0 ? "danger" : "default"}
          />
          <AdminStatCard label="Validade (dias)" value={validadeDias || "—"} icon={<CheckCircle2 />} />
        </div>

        {aviso ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            {aviso}
          </div>
        ) : null}
        {erro ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {erro}
          </div>
        ) : null}

        <AdminCard noPadding className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <select
              className={SELECT_CLASS}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Todos os status</option>
              <option value="pendente">Pendente</option>
              <option value="aprovado">Aprovado</option>
              <option value="rejeitado">Rejeitado</option>
            </select>
            <Input
              placeholder="Filtrar país (ISO-2)"
              value={pais}
              onChange={(e) => setPais(e.target.value.toUpperCase().slice(0, 2))}
              className="max-w-[160px]"
            />
            <div className="flex items-center gap-2 ml-auto">
              <Input
                placeholder="Novo destino (ex.: US)"
                value={novoPais}
                onChange={(e) => setNovoPais(e.target.value.toUpperCase().slice(0, 2))}
                className="max-w-[180px]"
              />
              <Button
                disabled={ocupado === "pesquisa"}
                className="bg-brand-sidebar hover:bg-brand-sidebar/90"
                onClick={pesquisar}
              >
                <Search className="w-3.5 h-3.5 mr-1.5" />
                {ocupado === "pesquisa" ? "Iniciando..." : "Apurar"}
              </Button>
            </div>
          </div>
        </AdminCard>

        <AdminCard noPadding className="p-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : regimes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhuma apuração com esses filtros. Use “Apurar” para iniciar um destino.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-4 font-semibold">Destino</th>
                    <th className="py-2 pr-4 font-semibold">Status</th>
                    <th className="py-2 pr-4 font-semibold">Confiança</th>
                    <th className="py-2 pr-4 font-semibold text-right">Sobretaxa</th>
                    <th className="py-2 pr-4 font-semibold">Válido até</th>
                    <th className="py-2 font-semibold" />
                  </tr>
                </thead>
                <tbody>
                  {regimes.map((r) => (
                    <Fragment key={r.id}>
                      <tr className="border-t border-slate-100 align-top">
                        <td className="py-3 pr-4">
                          <p className="font-medium text-slate-900">{r.pais_destino}</p>
                          <p className="text-[12px] text-slate-500">
                            apurado {data(r.created_at)}
                          </p>
                        </td>
                        <td className="py-3 pr-4">
                          <StatusBadge label={r.status} tone={TOM_STATUS[r.status] ?? "slate"} />
                          {(r.divergencias || []).length > 0 ? (
                            <p className="text-[12px] text-red-600 mt-1">rodadas divergiram</p>
                          ) : null}
                        </td>
                        <td className="py-3 pr-4">
                          {r.confianca ? (
                            <StatusBadge
                              label={r.confianca}
                              tone={TOM_CONFIANCA[r.confianca] ?? "slate"}
                            />
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-right tabular-nums">
                          {r.sobretaxa_total_pct != null ? `${r.sobretaxa_total_pct}%` : "—"}
                        </td>
                        <td className="py-3 pr-4 whitespace-nowrap">
                          <span className={r.vencido ? "text-red-600 font-medium" : "text-slate-700"}>
                            {data(r.valido_ate)}
                          </span>
                          {r.aprovado_por ? (
                            <p className="text-[12px] text-slate-500">por {r.aprovado_por}</p>
                          ) : null}
                        </td>
                        <td className="py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setExpandido(expandido === r.id ? null : r.id)}
                            >
                              {expandido === r.id ? "Fechar" : "Detalhes"}
                            </Button>
                            {r.status !== "aprovado" ? (
                              <Button
                                size="sm"
                                disabled={ocupado === r.id}
                                className="bg-brand-sidebar hover:bg-brand-sidebar/90"
                                onClick={() => aprovar(r)}
                              >
                                Aprovar
                              </Button>
                            ) : null}
                            {r.status !== "rejeitado" ? (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={ocupado === r.id}
                                onClick={() => rejeitar(r)}
                              >
                                Rejeitar
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {expandido === r.id ? (
                        <tr className="bg-slate-50/60">
                          <td colSpan={6} className="px-4 py-4">
                            <div className="flex flex-col gap-3 text-sm">
                              {r.resumo ? (
                                <p className="text-slate-700 whitespace-pre-wrap">{r.resumo}</p>
                              ) : null}
                              {(r.divergencias || []).length > 0 ? (
                                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                                  <p className="text-[12px] font-semibold text-red-700 mb-1">
                                    Divergências entre as rodadas
                                  </p>
                                  <ul className="list-disc pl-5 text-[13px] text-red-700">
                                    {(r.divergencias || []).map((d, i) => (
                                      <li key={i}>{String(d)}</li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null}
                              {(r.medidas || []).length > 0 ? (
                                <div>
                                  <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                                    Medidas
                                  </p>
                                  <pre className="text-[11px] text-slate-600 whitespace-pre-wrap break-all max-h-56 overflow-auto">
                                    {JSON.stringify(r.medidas, null, 2)}
                                  </pre>
                                </div>
                              ) : null}
                              {(r.fontes || []).length > 0 ? (
                                <div>
                                  <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                                    Fontes
                                  </p>
                                  <ul className="text-[13px] text-blue-700 flex flex-col gap-1">
                                    {(r.fontes || []).map((f, i) => (
                                      <li key={i}>
                                        {f.url ? (
                                          <a
                                            href={f.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="hover:underline break-all"
                                          >
                                            {f.titulo || f.url}
                                          </a>
                                        ) : (
                                          <span className="text-slate-600">{f.titulo}</span>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AdminCard>
      </div>
    </AdminLayout>
  );
};

export default AdminRegimes;
